import mixpanel from 'mixpanel-browser';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import CookieConsent from 'react-cookie-consent';
import 'url-search-params-polyfill';
import '../App.css';
import logo from '../assets/img/vucity-logo.png';

import ErrorMessage from '../components/ErrorMessage';
import FrameSession from '../components/FrameSession';
import LoginForm from '../components/Login';
import Rating from '../components/Rating';
import Slideshow from '../components/Slideshow/Slideshow';
import SpinnerMessage from '../components/SpinnerMessage';
import TechnicalIssue from '../components/TechnicalError';
import * as consts from '../constants';
import getFrameToken from '../getFrameToken';

import {
  Eighth,
  Fifth,
  First,
  Fourth,
  Second,
  Seventh,
  Sixth,
  Third,
} from '../components/exportImages';
import { Header, Link, Logo } from '../components/UI';

class Main extends Component {
  constructor() {
    super();

    this.state = {
      mode: consts.modes.spinner,
      status: 'Getting ready...',
      email: '',
      userToken: '',
      loginMethod: '',
      applications: {},
      selection: null,
      backend: null,
      hubRedirectUrl: null,
      frameToken: null,
    };
  }

  async componentDidMount() {
    // UNCOMMENT THIS FUNCTION CALL IF APP HAS TECHNICAL ISSUES
    // It will show a temporary message to users until problem is fixed
    // Can be used later to determine app status and automatically run it
    // this.isTechnicalIssue();

    // Grab the URL parameters
    const url = new URL(document.URL);
    const params = new URLSearchParams(url.search);

    // Read the backend from the referer
    // start with protocol://subdomain.vu(dev|stage|).city
    // '^\w+:\/\/': The string starts with a protocol e.g. 'https://'
    // '(?:\w+\.)*': Matches any number of subdomains without capturing e.g. 'any.thing.like.this.'
    // '(vu(?:dev|stage)?)': Captures vu, vudev, or vustage
    // '\.city': Matches our TLD
    // '(?:$|\/|\?)': Ensures that '.city' is the TLD
    const regex = /^\w+:\/\/(?:\w+\.)*(vu(?:dev|stage)?)\.city(?:$|\/|\?)/;
    const matches = regex.exec(document.referrer);
    if (matches != null) {
      this.setState({ backend: matches[1] });
    }

    // The dashboard will send us this so we can correctly attribute the
    // end-of-session ratings.
    // The email is also our identifier for MixPanel, so get it as early as
    // possible.
    const user = params.get('email');
    if (user) {
      const decodedEmail = decodeURIComponent(user);
      this.setState({ email: decodedEmail });
      mixpanel.identify(decodedEmail);
    } else {
      // Each unknown user counts as a unique user in Mixpanel which costs us
      // extra, so until we get the email log all analytics under the "unknown"
      // user.
      mixpanel.identify('unknown');
    }

    // Request the list of applications from the microservice
    const { accountID } = this.props;
    await this.loadApplications(accountID, true);

    // HACK: For beta users, select the hardcoded beta account automatically
    const betaUser = params.has('betauser');
    if (betaUser) {
      const { applications } = this.state;
      applications[consts.betaApp.id] = consts.betaApp;
      this.setState({
        applications,
        selection: consts.betaApp.id,
      });
    }

    // Allow setting the backend from the parameters as well as the referal URL
    const backend = params.get('backend');
    if (backend) {
      this.setState({ backend });
    }

    // The dashboard link will include the dashboard login ident
    // which we can pass into the app
    const ident = params.get('id');
    // The hub link will include an auth0 auth code which we can pass to the app
    const code = params.get('code');
    // We shouldn't auto login when there are technical issues
    const { mode } = this.state;
    if (mode === consts.modes.blocked) {
      // do nothing
    } else if (ident) {
      this.autoLogin(ident);
    } else if (code) {
      this.hubLogin(code);
    } else {
      this.setState({
        mode: consts.modes.loginDisallowed,
        userToken: '',
      });
    }

    // Secret code to enable the version selection box
    if (params.has('3dvisual')) {
      this.setState({ mode: consts.modes.login });
      // Don't need to await this
      this.loadApplications(consts.stagingAccountGCP.id, false);
      // Also pull AWS account as we test migrating
      this.loadApplications(consts.stagingAccountAWS.id, false);
    }
  }

  loadApplications = async (accountID, updateSelection) => {
    // Time how long this takes
    mixpanel.time_event(consts.eventNames.loadApps);

    const options = {
      method: 'POST',
      headers: {
        Authorization: process.env.REACT_APP_APP_LIST_SERVICE_AUTH,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ account_id: accountID }),
    };

    const response = await fetch(
      process.env.REACT_APP_APP_LIST_SERVICE_URL,
      options,
    );
    if (response.status === 200) {
      const payload = await response.json();

      // Append to the existing hash of applications
      this.setState((prevState) => ({
        applications: { ...prevState.applications, ...payload.applications },
      }));

      // Optionally update the selection state
      if (updateSelection) {
        this.setState({ selection: payload.dashboard_id });
      }
    } else if (updateSelection) {
      // HACK: Application service has failed, hardcoded the current known app
      const apps = {
        '6eeddca8-2eca-4460-be0f-ba2a8923ee1c': {
          displayName: 'VU.CITY Cloud',
          accountID: '408f8c47-1344-4459-a817-a825b0f601ba',
        },
      };

      this.setState({
        applications: apps,
        selection: '6eeddca8-2eca-4460-be0f-ba2a8923ee1c',
      });
    }

    // Analytics
    mixpanel.track(consts.eventNames.loadApps, {
      success: response.status === 200,
      status: response.status,
      account: consts.accounts[accountID].name,
    });
  };

  autoLogin = (ident) => {
    // TODO: aaiton 19-Dec-2019 - Need some auth on the ident
    this.setState({
      loginMethod: 'dashboard',
      userToken: ident,
    });
    this.showSpinner(consts.loggedIn);
    this.retrieveTokenAndStartFrameSession();
  };

  hubLogin = (code) => {
    // Remove the auth0 extras from our URL and store it
    const url = new URL(document.URL);
    url.searchParams.delete('code');
    url.searchParams.delete('state');

    this.setState({
      loginMethod: 'auth0',
      userToken: code,
      hubRedirectUrl: url,
    });
    this.showSpinner(consts.loggedIn);
    this.retrieveTokenAndStartFrameSession();
  }

  handleSelectionChange = (event) => {
    this.setState({ selection: event.target.value });
  };

  handleSuccessfulLogin = (email, ident) => {
    this.showSpinner(consts.loggedIn);
    this.setState({
      loginMethod: 'dashboard',
      userToken: ident,
      email,
    });

    this.retrieveTokenAndStartFrameSession();
  }

  setMode = (mode, status) => {
    this.setState({ mode, status });
  }

  setupUserData = () => {
    const {
      email,
      userToken,
      loginMethod,
      backend,
      hubRedirectUrl,
    } = this.state;

    let userData = '{ "error": "Missing Auth Token" }';
    if (userToken) {
      const user = loginMethod === 'auth0' ? hubRedirectUrl : email;

      // App feature flags
      const url = new URL(document.URL);
      const features = url.searchParams.getAll('feature');

      // Used by the hub to control how the app launches
      const project = url.searchParams.get('projectId');
      const file = url.searchParams.get('file');

      const payload = {
        user,
        token: userToken,
        method: loginMethod,
        features,
        backend,
        project,
        file,
      };
      userData = JSON.stringify(payload);
    }
    return userData;
  }

  retrieveTokenAndStartFrameSession = async () => {
    // Time token retrieval
    mixpanel.time_event(consts.eventNames.getToken);

    let frameToken = null;
    let message = '';
    try {
      const { email } = this.state;
      frameToken = await getFrameToken(email);
      // The FrameSession component will handle the rest
      this.setState({ mode: consts.modes.session, frameToken });
    } catch (err) {
      this.showError(`Couldn't authorise cloud session: ${err.message}`);
      message = err.message;
    }

    // Analytics
    mixpanel.track(consts.eventNames.getToken, {
      success: frameToken != null,
      message,
    });
    message = '';
  }

  isTechnicalIssue = () => {
    this.setState({ mode: consts.modes.blocked });
  }

  showSpinner = (message) => {
    this.setState({
      mode: consts.modes.spinner,
      status: message,
    });
  }

  showError = (message) => {
    this.setState({
      mode: consts.modes.error,
      status: message,
    });
  }

  render() {
    const {
      status,
      mode,
      email,
      applications,
      selection,
      backend,
      frameToken,
    } = this.state;

    let launchpadId = '';
    if (applications && selection) {
      launchpadId = consts.accounts[applications[selection].accountID].launchpad;
    }

    return (
      <div className="App">
        <div className="slide-show">
          <Slideshow slides={[First, Second, Third, Fourth, Fifth, Sixth, Seventh, Eighth]} />
        </div>
        <div className="content-area">
          <div className="app-header">
            <Logo url={null} imgSrc={logo} />
            <Header size="3">
              Welcome to VU.CITY Cloud
            </Header>
          </div>

          {
            {
              error: (<ErrorMessage message={status} />),

              login: (
                <LoginForm
                  onLogin={this.handleSuccessfulLogin}
                  onSelectionChange={this.handleSelectionChange}
                  selection={selection}
                  applications={applications}
                  backend={backend}
                />
              ),

              spinner: (<SpinnerMessage message={status} />),

              session: (
                <FrameSession
                  authToken={frameToken}
                  launchpadId={launchpadId}
                  appId={selection}
                  userData={this.setupUserData()}
                  setMode={this.setMode}
                />
              ),

              rating: (
                /* TODO: Extract email of login based on ident */
                <Rating
                  email={email || ''}
                  setMode={this.setMode}
                />
              ),

              blocked: (
                <TechnicalIssue>
                  VU.CITY Cloud is currently experiencing technical issues.{' '}
                  <br />
                  We are working to fix these as quickly as we can. <br />
                  Service will resume as soon as possible.
                </TechnicalIssue>
              ),

              loginDisallowed: (
                <TechnicalIssue>
                  It is no longer possible to login via this page.<br />
                  Please visit <Link href="https://dashboard.vu.city/dashboard/">the dashboard</Link> to access VU.CITY Cloud.
                </TechnicalIssue>
              ),
            }[mode]
          }
        </div>
        <CookieConsent
          style={{ background: 'var(--petrol-color)', alignItems: 'center' }}
          contentStyle={{ color: 'var(--white-color)' }}
          buttonStyle={{ fontSize: 'var(--large-font)' }}
        >
          In order to continually develop the platform, we gather user data to
          help us make optimal improvements to the platform. We never use your
          information for other purposes. We operate in full compliance with
          GDPR.
        </CookieConsent>
      </div>
    );
  }
}

Main.propTypes = {
  accountID: PropTypes.string.isRequired,
};

export default Main;
